import React, { useEffect, useState } from 'react';
import { User, Briefcase, ExternalLink, Eye, Copy, Edit, Upload, Camera, X, Check, Plus, Trash2, ArrowLeftSquareIcon, LogOut, Menu } from 'lucide-react';
import { fetchprofiledata, formatProfileLocation, updateDetails } from './profileAPIs';
import toast, { Toaster } from 'react-hot-toast';
import InstructorProfession from './Settings/instructorProfession';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import SocialLinksForm from './Settings/SocialLinksForm';
import ChangePassword from './Settings/changePassword';
import ReactQuill from 'react-quill-new';
// import 'react-quill-new/dist/quill.snow.css';
import './quill-modules';
import { profile } from 'console';

interface FormData {
    fullName: string;
    email: string;
    phone: string;
    dob: string;
    location: string;
    bio: string;
    languages: string[];
    profilePhoto: File | null;
    skill: string[];
    experience: string;
    certification: string[];
    mentorId: string;
    profileQuote: string;
    profileBanner: File | null;
    slug: string;
}

interface SidebarItem {
    id: string;
    label: string;
    icon: React.ComponentType<{ size?: number | string }>;
}

export default function InstructorProfileSettings() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);

    const getInitialTab = (): string => {
        const tabFromUrl = searchParams.get('tab');
        const validTabs = ['profile', 'professional', 'social', 'changePassword'];
        return validTabs.includes(tabFromUrl || '') ? tabFromUrl! : 'profile';
    };

    const [activeSection, setActiveSection] = useState<string>(getInitialTab());
    const [formData, setFormData] = useState<FormData>({
        fullName: '',
        email: '',
        phone: '',
        dob: '',
        location: '',
        bio: "",
        languages: ['English', 'Hindi', 'Marathi'],
        profilePhoto: null,
        skill: [],
        experience: "",
        certification: [],
        mentorId: "",
        profileQuote: "",
        profileBanner: null,
        slug: ""
    });

    const [newLanguage, setNewLanguage] = useState<string>('');
    const [editingLanguage, setEditingLanguage] = useState<number | null>(null);
    const [editLanguageText, setEditLanguageText] = useState<string>('');
    const [photoPreview, setPhotoPreview] = useState<string | null>(null);
    const [photoError, setPhotoError] = useState<string>('');
    const [backendProfileImage, setBackendProfileImage] = useState<string>('');
    const [hasNewUpload, setHasNewUpload] = useState<boolean>(false);
    const [showLogoutConfirmation, setShowLogoutConfirmation] = useState<boolean>(false);
    const [bannerPreview, setBannerPreview] = useState<string | null>(null);
    const [bannerError, setBannerError] = useState<string>('');

    const sidebarItems: SidebarItem[] = [
        { id: 'profile', label: 'Profile Info', icon: User },
        { id: 'professional', label: 'Professional', icon: Briefcase },
        { id: 'social', label: 'Social Links', icon: ExternalLink },
        { id: 'changePassword', label: 'Change Password', icon: Eye },
        { id: 'logout', label: 'Logout', icon: LogOut },
    ];

    const updateUrlTab = (tab: string) => {
        if (tab !== 'logout') {
            const newSearchParams = new URLSearchParams(searchParams);
            newSearchParams.set('tab', tab);
            setSearchParams(newSearchParams, { replace: true });
        }
    };

    useEffect(() => {
        const handlePopState = () => {
            const currentTab = getInitialTab();
            setActiveSection(currentTab);
        };

        const currentTab = getInitialTab();
        if (currentTab !== activeSection) {
            setActiveSection(currentTab);
        }

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, [searchParams]);

    useEffect(() => {
        const initialTab = getInitialTab();
        if (initialTab !== activeSection) {
            setActiveSection(initialTab);
        }
        if (!searchParams.get('tab')) {
            updateUrlTab('profile');
        }
    }, []);

    const handleInputChange = (field: keyof FormData, value: string | File | null): void => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleQuillChange = (field: keyof FormData, value: string): void => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const file = e.target.files?.[0];
        setPhotoError('');

        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setPhotoError('Please select a valid image file (JPG, PNG, GIF)');
            return;
        }

        if (file.size > 2 * 1024 * 1024) {
            setPhotoError('File size must be less than 2MB');
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            setPhotoPreview(event.target?.result as string);
            setHasNewUpload(true);
        };
        reader.readAsDataURL(file);

        handleInputChange('profilePhoto', file);
    };

    const handleBannerUpload = (e: React.ChangeEvent<HTMLInputElement>): void => {
        const file = e.target.files?.[0];
        setBannerError('');

        if (!file) return;

        if (!file.type.startsWith('image/')) {
            setBannerError('Please select a valid image file (JPG, PNG, GIF)');
            return;
        }

        if (file.size > 5 * 1024 * 1024) { // 5MB for banner
            setBannerError('File size must be less than 5MB');
            return;
        }

        const reader = new FileReader();
        reader.onload = (event) => {
            setBannerPreview(event.target?.result as string);
        };
        reader.readAsDataURL(file);

        handleInputChange('profileBanner', file);
    };

    const removePhoto = (): void => {
        setPhotoPreview(null);
        setPhotoError('');
        setHasNewUpload(false);
        handleInputChange('profilePhoto', null);

        const fileInput = document.getElementById('profilePhoto') as HTMLInputElement;
        if (fileInput) {
            fileInput.value = '';
        }
    };

    const getInitials = (name: string): string => {
        if (!name.trim()) return 'PS';
        const names = name.trim().split(' ');
        if (names.length === 1) return names[0].charAt(0).toUpperCase();
        return (names[0].charAt(0) + names[names.length - 1].charAt(0)).toUpperCase();
    };

    const getCurrentDisplayImage = (): string | null => {
        if (hasNewUpload && photoPreview) {
            return photoPreview;
        }
        if (backendProfileImage) {
            return backendProfileImage;
        }
        return null;
    };

    const handleFetchDetails = async () => {
        const res = await fetchprofiledata();
        if (res?.profile) {
            const profileData = {
                firstname: res.profile.firstName ?? "",
                lastname: res.profile.lastName ?? "",
                email: res.profile.email ?? "",
                phone: res.profile.phone ?? "",
                dob: res.profile.dob ?? "",
                location: formatProfileLocation(res.profile.location),
                bio: res.profile.bio ?? "",
                languages: res.profile.languages ?? [],
                profilePhoto: res.profile.profileIcon ?? "",
                certification: res.profile.certification ?? [],
                skills: res.profile.skills ?? [],
                experience: res.profile.experience ?? "",
                mentorId: res.profile.mentorId ?? "",
                profileQuote: res.profile.profileQuote ?? "",
                profileBanner: res.profile.profileBanner ?? "",
                slug: res.profile.slug
            };
            if (profileData.profilePhoto) {
                setBackendProfileImage(profileData.profilePhoto);
            }
            if (profileData.profileBanner) {
                setBannerPreview(profileData.profileBanner);
            }
            let locationStr = "";
            if (profileData.location) {
                if (typeof profileData.location === 'object') {
                    locationStr = [
                        (profileData.location as any).address,
                        (profileData.location as any).city,
                        (profileData.location as any).state,
                        (profileData.location as any).country
                    ].filter(Boolean).join(', ');
                } else {
                    locationStr = profileData.location;
                }
            }

            setFormData(prev => ({
                ...prev,
                fullName: `${profileData.firstname} ${profileData.lastname}`.trim(),
                email: profileData.email,
                phone: profileData.phone,
                dob: profileData.dob,
                location: locationStr,
                bio: profileData.bio,
                languages: profileData.languages.length > 0 ? profileData.languages : prev.languages,
                certification: profileData.certification.length > 0 ? profileData.certification : prev.certification,
                skill: profileData.skills.length > 0 ? profileData.skills : prev.skill,
                experience: profileData.experience,
                mentorId: profileData.mentorId,
                profileQuote: profileData.profileQuote,
                slug: profileData.slug
            }));
        } else {
            navigate("/", { replace: true })
        }
    };

    useEffect(() => {
        handleFetchDetails();
    }, []);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const form = new FormData();

        form.append('fullName', formData.fullName);
        form.append('email', formData.email);
        form.append('phone', formData.phone);
        form.append('dob', formData.dob);
        form.append('location', formData.location);
        form.append('bio', formData.bio);
        form.append('languages', JSON.stringify(formData.languages));
        form.append('profileQuote', formData.profileQuote);

        if (formData.profilePhoto) {
            form.append('profilePhoto', formData.profilePhoto as Blob);
        }

        if (formData.profileBanner) {
            form.append('profileBanner', formData.profileBanner as Blob);
        }

        form.append('userType', 'instructor');

        try {
            const res = await updateDetails(form);

            if (res.status === 200 && hasNewUpload && photoPreview) {
                setBackendProfileImage(photoPreview);
                setHasNewUpload(false);
                setFormData(prev => ({ ...prev, profilePhoto: null }));
            }
            toast.success("Details Updated Successfully!");
        } catch (error) {
            console.error('Error updating profile:', error);
        }
    };

    const addLanguage = (): void => {
        if (newLanguage.trim() && !formData.languages.includes(newLanguage.trim())) {
            setFormData(prev => ({
                ...prev,
                languages: [...prev.languages, newLanguage.trim()]
            }));
            setNewLanguage('');
        }
    };

    const removeLanguage = (indexToRemove: number): void => {
        setFormData(prev => ({
            ...prev,
            languages: prev.languages.filter((_, index) => index !== indexToRemove)
        }));
    };

    const startEditLanguage = (index: number, language: string): void => {
        setEditingLanguage(index);
        setEditLanguageText(language);
    };

    const saveEditLanguage = (): void => {
        if (editLanguageText.trim() && !formData.languages.includes(editLanguageText.trim())) {
            setFormData(prev => ({
                ...prev,
                languages: prev.languages.map((lang, index) =>
                    index === editingLanguage ? editLanguageText.trim() : lang
                )
            }));
        }
        setEditingLanguage(null);
        setEditLanguageText('');
    };

    const cancelEditLanguage = (): void => {
        setEditingLanguage(null);
        setEditLanguageText('');
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, action: 'add' | 'save' | 'cancel'): void => {
        if (action === 'add' && e.key === 'Enter') {
            e.preventDefault();
            addLanguage();
        } else if (action === 'save') {
            if (e.key === 'Enter') {
                e.preventDefault();
                saveEditLanguage();
            } else if (e.key === 'Escape') {
                cancelEditLanguage();
            }
        }
    };

    const handleLogout = () => {
        try {
            sessionStorage.clear();
            toast.success("Logged out successfully!");
            setTimeout(() => {
                navigate('/', { replace: true });
                window.location.reload();
            }, 1500);
        } catch (error) {
            console.error('Error during logout:', error);
            toast.error("Error during logout. Please try again.");
        }
    };

    const handleSectionChange = (sectionId: string) => {
        if (sectionId === 'logout') {
            setShowLogoutConfirmation(true);
        } else {
            setActiveSection(sectionId);
            updateUrlTab(sectionId);
            setIsMobileMenuOpen(false);
        }
    };

    const confirmLogout = () => {
        setShowLogoutConfirmation(false);
        handleLogout();
    };

    const cancelLogout = () => {
        setShowLogoutConfirmation(false);
    };

    const getTabDisplayName = (tabId: string): string => {
        const item = sidebarItems.find(item => item.id === tabId);
        return item?.label || 'Settings';
    };

    const currentDisplayImage = getCurrentDisplayImage();

    const modules = (maxLength: number) => ({
        maxLength: { maxLength },
    });

    return (
        <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 p-3 sm:p-6">
            <Toaster />

            {showLogoutConfirmation && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
                        <div className="text-center">
                            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                                <LogOut className="h-6 w-6 text-red-600" />
                            </div>
                            <h3 className="text-lg font-medium text-gray-900 mb-2">
                                Confirm Logout
                            </h3>
                            <p className="text-sm text-gray-500 mb-6">
                                Are you sure you want to logout? You will need to login again to access your account.
                            </p>
                            <div className="flex gap-3 justify-center">
                                <button
                                    type="button"
                                    onClick={cancelLogout}
                                    className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={confirmLogout}
                                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                                >
                                    Logout
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6 mb-6">
                    <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
                        <div className="flex items-center gap-4">
                            <Link to="/instructor-profile" className="text-gray-400 hover:text-purple-600 transition-colors" title="Back to Profile">
                                <ArrowLeftSquareIcon size={28} className="sm:w-8 sm:h-8" />
                            </Link>
                            <div className="flex-1">
                                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Profile Information</h1>
                                <p className="text-gray-600 text-xs sm:text-sm mt-1">
                                    <span className="hidden sm:inline">Manage your profile, preferences, and account security</span>
                                    <span className="sm:hidden">Manage your account settings</span>
                                    {activeSection !== 'profile' && (
                                        <span className="text-purple-600 font-medium ml-2">
                                            • {getTabDisplayName(activeSection)}
                                        </span>
                                    )}
                                </p>
                            </div>
                            {/* Mobile menu button */}
                            <button
                                type="button"
                                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                                className="lg:hidden p-2 text-gray-600 hover:text-purple-600 transition-colors"
                            >
                                <Menu size={24} />
                            </button>
                        </div>
                        <div className="flex gap-2 sm:gap-3">
                            <button
                                type="button"
                                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                                onClick={() => {
                                    const profileLink = `${process.env.REACT_APP_API_FRONTEND_URL}/public/instructor-profile/${formData.slug}`;
                                    navigator.clipboard.writeText(profileLink);
                                    toast.success("Profile link copied!");
                                }}>
                                <Copy size={14} className="sm:w-4 sm:h-4" />
                                <span className="hidden sm:inline">Copy Profile Link</span>
                                <span className="sm:hidden">Copy Link</span>
                            </button>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col lg:flex-row gap-6">
                    {/* Mobile Sidebar Overlay */}
                    {isMobileMenuOpen && (
                        <div
                            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
                            onClick={() => setIsMobileMenuOpen(false)}
                        />
                    )}

                    {/* Sidebar */}
                    <div className={`
                        lg:w-80 lg:block
                        ${isMobileMenuOpen ? 'block' : 'hidden'}
                        lg:relative fixed top-0 left-0 h-full lg:h-auto z-50 lg:z-auto
                        w-64 lg:w-80
                    `}>
                        <div className="bg-white rounded-2xl shadow-sm p-4 lg:sticky lg:top-6 h-full lg:h-auto overflow-y-auto">
                            {/* Mobile close button */}
                            <div className="lg:hidden flex justify-between items-center mb-4 pb-4 border-b">
                                <h3 className="font-semibold text-gray-900">Menu</h3>
                                <button
                                    onClick={() => setIsMobileMenuOpen(false)}
                                    className="text-gray-400 hover:text-gray-600"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            <nav>
                                <ul className="space-y-2">
                                    {sidebarItems.map((item) => {
                                        const IconComponent = item.icon;
                                        const isActive = activeSection === item.id && item.id !== 'logout';
                                        const isLogoutItem = item.id === 'logout';

                                        return (
                                            <li key={item.id}>
                                                <button
                                                    type="button"
                                                    onClick={() => handleSectionChange(item.id)}
                                                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-xl text-left transition-all ${isActive
                                                        ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
                                                        : isLogoutItem
                                                            ? 'text-red-600 hover:bg-red-50'
                                                            : 'text-gray-700 hover:bg-gray-100'
                                                        }`}
                                                >
                                                    <IconComponent size={20} />
                                                    <span className="font-medium">{item.label}</span>
                                                </button>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </nav>
                        </div>
                    </div>

                    {/* Main Content */}
                    <div className="flex-1">
                        {activeSection === "profile" && (
                            <form onSubmit={handleSubmit} encType='multipart/form-data' className="bg-white rounded-2xl shadow-sm p-4 sm:p-8">
                                <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-8">
                                    <h2 className="text-lg sm:text-xl font-bold text-gray-900">Profile Information</h2>
                                </div>

                                {/* Profile Photo Section */}
                                <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6 mb-8">
                                    <div className="relative mx-auto sm:mx-0">
                                        {currentDisplayImage ? (
                                            <div className="relative">
                                                <img
                                                    src={currentDisplayImage}
                                                    alt="Profile"
                                                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-2 border-gray-200" />
                                                {hasNewUpload && (
                                                    <button
                                                        type="button"
                                                        onClick={removePhoto}
                                                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                                                        title="Remove new photo">
                                                        <X size={12} />
                                                    </button>
                                                )}
                                            </div>
                                        ) : (
                                            <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gradient-to-br from-purple-600 to-pink-600 rounded-full flex items-center justify-center text-white text-xl sm:text-2xl font-bold">
                                                {getInitials(formData.fullName)}
                                            </div>
                                        )}

                                        <label
                                            htmlFor="profilePhoto"
                                            className="absolute -bottom-1 -right-1 w-7 h-7 sm:w-8 sm:h-8 bg-teal-400 rounded-full flex justify-center items-center cursor-pointer hover:bg-teal-500 transition-colors shadow-lg"
                                            title="Change photo"
                                        >
                                            <Camera size={14} className="sm:w-4 sm:h-4 text-white" />
                                        </label>
                                        <input
                                            type="file"
                                            name="profilePhoto"
                                            id="profilePhoto"
                                            className="hidden"
                                            accept="image/*"
                                            onChange={handleFileUpload}
                                        />
                                    </div>
                                    <div className="flex-1 text-center sm:text-left">
                                        <h3 className="font-semibold text-gray-900 mb-1">Profile Photo</h3>
                                        <p className="text-sm text-gray-500 mb-3">Accepts JPG, JPEG, or PNG.</p>

                                        {photoError && (
                                            <div className="text-red-500 text-sm mb-3 p-2 bg-red-50 rounded-lg border border-red-200">
                                                {photoError}
                                            </div>
                                        )}

                                        {hasNewUpload && (
                                            <div className="text-green-600 text-sm mb-3 p-2 bg-green-50 rounded-lg border border-green-200">
                                                New photo selected. Save changes to update your profile.
                                            </div>
                                        )}

                                        <div className="flex flex-col sm:flex-row gap-2">
                                            <label
                                                htmlFor="profilePhoto"
                                                className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                                            >
                                                <Upload size={16} />
                                                {currentDisplayImage ? 'Change Photo' : 'Upload Photo'}
                                            </label>

                                            {hasNewUpload && (
                                                <button
                                                    type="button"
                                                    onClick={removePhoto}
                                                    className="flex items-center justify-center gap-2 px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors"
                                                >
                                                    <Trash2 size={16} />
                                                    Cancel
                                                </button>
                                            )}
                                        </div>

                                        {formData.profilePhoto && (
                                            <div className="mt-2 text-sm text-gray-600">
                                                <span className="font-medium">Selected:</span> {formData.profilePhoto.name}
                                                <span className="text-gray-400 ml-2">
                                                    ({(formData.profilePhoto.size / 1024 / 1024).toFixed(2)} MB)
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                {/* Profile Banner */}
                                <div className="my-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Profile Banner
                                    </label>
                                    <div className="flex flex-col gap-4">
                                        <input
                                            type="file"
                                            name="profileBanner"
                                            id="profileBanner"
                                            className="hidden"
                                            accept="image/*"
                                            onChange={handleBannerUpload}
                                        />
                                        <label
                                            htmlFor="profileBanner"
                                            className="flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer w-fit"
                                        >
                                            <Upload size={16} />
                                            Upload Banner
                                        </label>
                                        {bannerPreview && (
                                            <div className="w-full">
                                                <img
                                                    src={bannerPreview}
                                                    alt="Banner Preview"
                                                    className="h-32 sm:h-40 md:h-48 lg:h-[24vh] w-full object-cover rounded-lg border border-gray-200"
                                                />
                                            </div>
                                        )}
                                    </div>
                                    {bannerError && (
                                        <p className="text-red-500 text-sm mt-2">{bannerError}</p>
                                    )}
                                </div>

                                {/* Form Fields */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Full Name
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.fullName}
                                            onChange={(e) => handleInputChange('fullName', e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                                            placeholder="Enter your full name"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Email
                                        </label>
                                        <input
                                            type="email"
                                            value={formData.email}
                                            onChange={(e) => handleInputChange('email', e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                                            placeholder="Enter your email"
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Phone Number
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.phone}
                                            onChange={(e) => handleInputChange('phone', e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                                            placeholder="Enter your phone number"
                                            maxLength={10}
                                            pattern="[0-9]*"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Date of Birth
                                        </label>
                                        <input
                                            type="date"
                                            value={formData.dob}
                                            onChange={(e) => handleInputChange('dob', e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                                            max={new Date().toISOString().split("T")[0]} // prevents future dates
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Location
                                        </label>
                                        <input
                                            type="text"
                                            value={formData.location}
                                            onChange={(e) => handleInputChange('location', e.target.value)}
                                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                                            placeholder="Enter your location"
                                        />
                                    </div>
                                </div>

                                {/* Profile Quote */}
                                <div className="mt-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Profile Quote
                                    </label>
                                    <ReactQuill
                                        theme="snow"
                                        value={formData.profileQuote}
                                        onChange={(value) => handleQuillChange('profileQuote', value)}
                                        className="w-full h-32 bg-white rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                                        placeholder="Enter a short, impactful quote..."
                                        modules={modules(80)}
                                    />
                                    <p className="text-sm text-gray-500 mt-12 sm:mt-2">
                                        {formData.profileQuote.replace(/<[^>]*>?/gm, '').length} / 80 characters
                                    </p>
                                </div>

                                {/* Bio */}
                                <div className="mt-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Bio
                                    </label>
                                    <textarea
                                        value={formData.bio}
                                        onChange={(e) => handleInputChange('bio', e.target.value)}
                                        rows={4}
                                        maxLength={200}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                                        placeholder="Tell us about yourself and your expertise..." />
                                    <p className="text-sm text-gray-500 mt-2">
                                        {formData.bio.length} / 200 characters
                                    </p>
                                </div>

                                {/* Languages */}
                                <div className="mt-6">
                                    <label className="block text-sm font-medium text-gray-700 mb-3">
                                        Languages
                                    </label>
                                    <div className="flex flex-wrap gap-2 mb-4">
                                        {formData.languages.map((lang, index) => (
                                            <div key={index} className="relative group">
                                                {editingLanguage === index ? (
                                                    <div className="flex items-center gap-2 bg-purple-100 rounded-full px-3 py-1">
                                                        <input
                                                            type="text"
                                                            value={editLanguageText}
                                                            onChange={(e) => setEditLanguageText(e.target.value)}
                                                            className="bg-transparent text-purple-700 text-sm font-medium outline-none w-20"
                                                            onKeyDown={(e) => handleKeyDown(e, 'save')}
                                                            autoFocus
                                                        />
                                                        <button
                                                            type="button"
                                                            onClick={saveEditLanguage}
                                                            className="text-green-600 hover:text-green-700 transition-colors"
                                                        >
                                                            <Check size={14} />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={cancelEditLanguage}
                                                            className="text-red-600 hover:text-red-700 transition-colors"
                                                        >
                                                            <X size={14} />
                                                        </button>
                                                    </div>
                                                ) : (
                                                    <span
                                                        className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-medium cursor-pointer hover:bg-purple-200 transition-colors flex items-center gap-2"
                                                        onClick={() => startEditLanguage(index, lang)}
                                                    >
                                                        {lang}
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                removeLanguage(index);
                                                            }}
                                                            className="text-purple-500 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                                                        >
                                                            <X size={14} />
                                                        </button>
                                                    </span>
                                                )}
                                            </div>
                                        ))}
                                    </div>

                                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                                        <input
                                            type="text"
                                            value={newLanguage}
                                            onChange={(e) => setNewLanguage(e.target.value)}
                                            placeholder="Add a language..."
                                            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none transition-all"
                                            onKeyDown={(e) => handleKeyDown(e, 'add')}
                                        />
                                        <button
                                            type="button"
                                            onClick={addLanguage}
                                            disabled={!newLanguage.trim()}
                                            className="flex items-center justify-center gap-1 px-3 py-2 bg-purple-600 text-white rounded-lg text-sm hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <Plus size={14} />
                                            Add
                                        </button>
                                    </div>

                                    <p className="text-xs text-gray-500 mt-2">
                                        Click on a language to edit it, or use the × button to remove it
                                    </p>
                                </div>


                                {/* Form Actions */}
                                <div className="flex flex-col sm:flex-row justify-end gap-4 mt-8 pt-6 border-t border-gray-200">
                                    <button
                                        type="button"
                                        className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors order-2 sm:order-1"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-all shadow-lg order-1 sm:order-2"
                                    >
                                        Save Changes
                                    </button>
                                </div>
                            </form>
                        )}
                        {activeSection === "professional" && (
                            <InstructorProfession
                                skill={formData.skill}
                                experience={formData.experience}
                                certification={formData.certification}
                            />
                        )}
                        {activeSection === "social" && (
                            <SocialLinksForm />
                        )}
                        {activeSection === "changePassword" && (
                            <ChangePassword />
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
